import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, qs } from '../../lib/api';
import { useDebounced, useListState } from '../../lib/hooks';
import { date, money } from '../../lib/format';
import { fxMoney, rateText } from '../../lib/currency';
import { EmptyState, ErrorState, PageHead, Stat, TableSkeleton } from '../../components/ui';
import { DateRangeFilter } from '../../components/DateRangeFilter';
import { ExportMenu } from '../../components/ExportMenu';
import { Icon } from '../../components/Icon';
import { Code, CodeText } from '../../components/Code';
import { exportDay, type TableExportDoc } from '../../lib/tableExport';
import { copyInvoiceLink, invoiceShareUrl } from '../../lib/shareLinks';
import { useToast } from '../../components/Toast';

/* ==========================================================================
   كشف حساب عميل أو مورد
   ========================================================================== */

type Side = 'customer' | 'supplier';

interface StatementDocument {
  type: 'INVOICE' | 'PAYMENT' | 'BILL' | 'BILL_PAYMENT' | 'EXPENSE';
  label: string;
  id: string;
  number: string;
  billNumber?: string | null;
  title: string | null;
  /** فاتورةُ العميل التي يفتحها رابطُه — الفاتورةُ نفسها أو التي سدّدها التحصيل. */
  invoiceId?: string | null;
  shareToken?: string | null;
}

interface StatementRow {
  id: string;
  entryId: string;
  date: string;
  number: string;
  sourceLabel: string;
  reference: string | null;
  document: StatementDocument | null;
  /** رقمُ المستند (أو مرجعُ القيد بلا مستند) — عمودُ «المستند». */
  documentNumber: string;
  /** عنوانُه: اسمُ المشروع على الفاتورة، والفاتورةُ التي سدّدها التحصيل… */
  documentTitle: string;
  /** ما بقي من البيان — سطرٌ ثانٍ أصغر تحت العنوان. */
  details: string;
  description: string;
  debit: number;
  credit: number;
  balance: number;
  direct: boolean;
  fx: { currency: string; amount: number; rate: number | null } | null;
}

interface Statement {
  party: {
    id: string;
    name: string;
    legalName: string | null;
    phone: string | null;
    email: string | null;
    address: string | null;
    taxNumber: string | null;
  };
  opening: number;
  rows: StatementRow[];
  totals: { debit: number; credit: number };
  closing: number;
}

const SIDES: Record<
  Side,
  {
    tab: string;
    party: string;
    title: string;
    pick: string;
    search: string;
    debit: string;
    credit: string;
    debitTotal: string;
    creditTotal: string;
    closingNote: (n: number) => string;
  }
> = {
  customer: {
    tab: 'العملاء',
    party: 'العميل',
    title: 'كشف حساب عميل',
    pick: 'اختر العميل',
    search: '/crm/accounts',
    /* العميل: المدين فواتيره واستحقاقاته، والدائن ما دفعه. */
    debit: 'مدين — فواتير',
    credit: 'دائن — مدفوعات',
    debitTotal: 'إجمالي الفواتير',
    creditTotal: 'إجمالي الدفعات',
    closingNote: (n) => (n > 0 ? 'مستحق لنا على العميل' : n < 0 ? 'رصيد دائن للعميل' : 'الحساب مسدّد'),
  },
  supplier: {
    tab: 'الموردون',
    party: 'المورد',
    title: 'كشف حساب مورد',
    pick: 'اختر المورد',
    search: '/finance/suppliers',
    /* المورد بالعكس: الدائن فواتيره ومشترياته، والمدين ما سدّدناه له. */
    debit: 'دائن — مشتريات',
    credit: 'مدين — مدفوعات',
    debitTotal: 'إجمالي المشتريات',
    creditTotal: 'إجمالي الدفعات',
    closingNote: (n) => (n > 0 ? 'مستحق علينا للمورد' : n < 0 ? 'رصيد مدين على المورد' : 'الحساب مسدّد'),
  },
};

/** رابطُ المستند: الفاتورةُ بصفحتها، وغيرُها بقائمته مبحوثًا برقمه. */
function documentLink(d: StatementDocument): string {
  switch (d.type) {
    case 'INVOICE':
      return `/finance/invoices/${d.id}`;
    case 'PAYMENT':
      return `/finance/payments${qs({ q: d.number })}`;
    case 'BILL':
      return `/finance/bills${qs({ q: d.number })}`;
    case 'BILL_PAYMENT':
      return `/finance/bills${qs({ q: d.billNumber ?? d.number })}`;
    case 'EXPENSE':
      return `/finance/expenses${qs({ q: d.number })}`;
  }
}

const fxText = (fx: StatementRow['fx']) =>
  fx ? `${fxMoney(fx.amount, fx.currency)}${fx.rate ? ` × ${rateText(fx.rate)}` : ''}` : '';

/**
 * سطرُ التفاصيل تحت عنوان المستند: ما بقي من البيان (الخادمُ يحذف منه ما
 * يكرّر رقمَ المستند) والمبلغُ بعملته الأجنبيّة — أصغرُ وأخفت، فلا يطول الصفّ.
 */
const subLine = (r: StatementRow) => [r.details, r.fx ? fxText(r.fx) : ''].filter(Boolean).join(' · ');

/** رابطُ الكشف مُعبّأً — تفتحه صفحاتُ الأرصدة وملفُّ العميل والموردون. */
export function statementPath(side: Side, party: string, range: { from?: string; to?: string } = {}): string {
  return `${STATEMENT_PATHS[side]}${qs({ party, from: range.from, to: range.to })}`;
}

/** لكلّ جهةٍ صفحتُها في القائمة: كشفُ العملاء بجوار أرصدتهم، والموردين بجوار أرصدتهم. */
export const STATEMENT_PATHS: Record<Side, string> = {
  customer: '/finance/statements',
  supplier: '/finance/supplier-statements',
};

/**
 * كشف حساب عميل أو مورد.
 *
 * أكثر ما يطلبه العميل: كل ما اشتراه بفواتيره، وكل ما دفعه، بتاريخه، والرصيد
 * بعد كل حركة. يُقرأ من الدفتر بأساس تقرير الأرصدة نفسه، فالرصيد الختامي هو
 * ما تُظهره «أرصدة العملاء/الموردين» للطرف في التاريخ نفسه.
 *
 * الحالة كلها في الرابط (الطرف والجهة والمدة)، فيُرسل الكشف رابطًا ويُفتح
 * كما هو. والمدة الافتراضية من أول السنة إلى اليوم.
 */
export function PartyStatement({ side }: { side: Side }) {
  const defaults = useMemo(() => {
    const today = new Date();
    return {
      from: `${today.getFullYear()}-01-01`,
      to: exportDay(today.toISOString()) ?? '',
    };
  }, []);
  const { state, patch } = useListState(defaults);
  const config = SIDES[side];
  const party = state.party;
  const { from, to } = state;

  const statement = useQuery({
    queryKey: ['party-statement', side, party, from, to],
    queryFn: () => api<Statement>(`/accounting/statements/${side}/${party}${qs({ from, to })}`),
    enabled: Boolean(party),
  });
  const data = statement.data;
  const rows = data?.rows ?? [];
  const toast = useToast();

  const buildExport = (): TableExportDoc => {
    const s = data!;
    const p = s.party;
    return {
      title: `${config.title} — ${p.name}`,
      /* يُطبع على «ورقة كشف الحساب» إن رُفعت في الإعدادات، وإلّا على العامّة. */
      paper: 'statement',
      sheets: [
        {
          title: config.title,
          period: { from: from || undefined, to: to || undefined },
          details: [
            { label: config.party, value: p.legalName && p.legalName !== p.name ? `${p.name} (${p.legalName})` : p.name },
            ...(p.phone ? [{ label: 'الهاتف', value: p.phone }] : []),
            ...(p.email ? [{ label: 'البريد', value: p.email }] : []),
            ...(p.address ? [{ label: 'العنوان', value: p.address }] : []),
            ...(p.taxNumber ? [{ label: 'الرقم الضريبي', value: p.taxNumber }] : []),
            { label: 'الرصيد الختامي', value: `${money(s.closing)} — ${config.closingNote(s.closing)}` },
          ],
          sections: [
            {
              columns: [
                { key: 'date', label: 'التاريخ', kind: 'date' },
                /*
                 * رقمُ المستند رابطٌ (`documentHref`): العميلُ ينقره في PDF أو إكسل
                 * فتُفتح فاتورتُه كاملةً — بلا حسابٍ في النظام.
                 */
                { key: 'document', label: 'المستند', kind: 'code', hrefKey: 'documentHref' },
                /* عنوانُ المستند (اسمُ المشروع) سطرًا رئيسًا، وتفاصيلُ البيان تحته أصغر. */
                { key: 'title', label: 'عنوان المستند', minWidth: 34 },
                { key: 'debit', label: config.debit, kind: 'money' },
                { key: 'credit', label: config.credit, kind: 'money' },
                { key: 'balance', label: 'الرصيد', kind: 'money' },
              ],
              rows: [
                { _style: 'heading', date: from || null, title: 'رصيد أول المدة', balance: s.opening },
                ...s.rows.map((r) => ({
                  date: exportDay(r.date),
                  document: r.documentNumber,
                  documentHref: r.document?.shareToken ? invoiceShareUrl(r.document.shareToken) : null,
                  /* السطرُ الثاني تفاصيلُ البيان والعملةُ الأجنبيّة — أصغرُ وأخفتُ في PDF. */
                  title: subLine(r) ? `${r.documentTitle}\n${subLine(r)}` : r.documentTitle,
                  debit: r.debit || null,
                  credit: r.credit || null,
                  balance: r.balance,
                })),
              ],
              totals: {
                title: 'إجمالي الحركة والرصيد الختامي',
                debit: s.totals.debit,
                credit: s.totals.credit,
                balance: s.closing,
              },
            },
          ],
          notes: [
            `رصيد أول المدة ${money(s.opening)} + ${config.debitTotal} ${money(s.totals.debit)} − ${config.creditTotal} ${money(s.totals.credit)} = الرصيد الختامي ${money(s.closing)}.`,
            ...(s.rows.some((r) => r.document?.shareToken)
              ? ['رقم الفاتورة رابطٌ: انقره لتُفتح الفاتورة كاملةً ببنودها ومدفوعاتها.']
              : []),
            ...(s.rows.some((r) => r.direct)
              ? ['الشراء النقدي المباشر يظهر فاتورةً ودفعةً في سطرٍ واحد، فلا يغيّر الرصيد.']
              : []),
          ],
        },
      ],
    };
  };

  return (
    <div className="page">
      <PageHead
        title={side === 'customer' ? 'كشف حساب عميل' : 'كشف حساب مورد'}
        subtitle="كل الفواتير والدفعات بتاريخها والرصيد بعد كل حركة — من واقع دفتر الأستاذ"
      />

      <div className="toolbar sticky-bar">
        <PartyPicker
          key={side}
          side={side}
          value={party ? (data?.party.name ?? '…') : ''}
          onPick={(id) => patch({ party: id })}
        />
        <DateRangeFilter
          from={from}
          to={to}
          /* الطرفُ الممسوح يعود إلى الافتراضيّ (أول السنة، اليوم) — من `useListState`. */
          onChange={(f, t) => patch({ from: f, to: t })}
        />
        <ExportMenu build={buildExport} disabled={!data} />
        {data && (
          <Link
            to={side === 'customer' ? `/crm/accounts/${data.party.id}` : `/finance/suppliers${qs({ q: data.party.name })}`}
            className="btn btn-ghost"
            style={{ marginInlineStart: 'auto' }}
          >
            <Icon name="users" size={15} />
            {side === 'customer' ? 'ملف العميل' : 'بيانات المورد'}
          </Link>
        )}
      </div>

      {!party && (
        <div className="card">
          <EmptyState
            title={config.pick}
            text={`اختر ${side === 'customer' ? 'عميلًا' : 'موردًا'} من القائمة أعلاه لعرض كشف حسابه في المدة المحددة.`}
          />
        </div>
      )}

      {statement.isError && <ErrorState error={statement.error} onRetry={() => void statement.refetch()} />}

      {party && (
        <>
          <div className="stats">
            <Stat label="رصيد أول المدة" value={<span className="num">{data ? money(data.opening) : '—'}</span>} />
            <Stat
              label={config.debitTotal}
              value={<span className="num">{data ? money(data.totals.debit) : '—'}</span>}
            />
            <Stat
              label={config.creditTotal}
              value={<span className="num">{data ? money(data.totals.credit) : '—'}</span>}
            />
            <Stat
              label="الرصيد الختامي"
              value={<span className="num">{data ? money(data.closing) : '—'}</span>}
              note={data ? config.closingNote(data.closing) : undefined}
            />
          </div>

          <div className="card">
            <div className="table-wrap sticky-head">
              <table className="table">
                <thead>
                  <tr>
                    <th style={{ width: 110 }}>التاريخ</th>
                    <th style={{ width: 170 }}>المستند</th>
                    <th>عنوان المستند</th>
                    <th className="col-num" style={{ width: 140 }}>
                      {config.debit}
                    </th>
                    <th className="col-num" style={{ width: 140 }}>
                      {config.credit}
                    </th>
                    <th className="col-num" style={{ width: 150 }}>
                      الرصيد
                    </th>
                  </tr>
                </thead>

                {statement.isPending && <TableSkeleton cols={6} />}

                {data && (
                  <tbody>
                    <tr>
                      <td className="num cell-mute">{from ? date(from) : '—'}</td>
                      <td />
                      <td className="cell-strong">رصيد أول المدة</td>
                      <td />
                      <td />
                      <td className={`num cell-strong${data.opening < 0 ? ' neg' : ''}`}>{money(data.opening)}</td>
                    </tr>

                    {rows.map((r) => (
                      <tr key={r.id}>
                        <td className="num cell-mute">{date(r.date)}</td>
                        <td className="num">
                          {/*
                            المستندُ يفتح صفحتَه للفريق، وبلا مستندٍ فالقيدُ في الدفتر.
                            ورابطُ العميل (الفاتورةُ العامّة) يُنسخ من الزرّ بجواره.
                          */}
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            <Link
                              to={r.document ? documentLink(r.document) : `/finance/journal${qs({ q: r.number })}`}
                              className="link"
                              title={`${r.document?.label ?? r.sourceLabel} — القيد ${r.number}`}
                            >
                              <Code>{r.documentNumber}</Code>
                            </Link>
                            {r.document?.shareToken && (
                              <button
                                type="button"
                                className="btn btn-ghost btn-icon btn-sm"
                                title="نسخ رابط العميل — يفتح الفاتورة كاملةً بلا حساب"
                                aria-label="نسخ رابط العميل"
                                onClick={() => void copyInvoiceLink(r.document!.shareToken!, toast)}
                              >
                                <Icon name="link" size={13} />
                              </button>
                            )}
                          </span>
                        </td>
                        <td>
                          <CodeText text={r.documentTitle} />
                          {subLine(r) && (
                            <span className="cell-sub">
                              <CodeText text={subLine(r)} />
                            </span>
                          )}
                        </td>
                        <td className="num">{r.debit ? money(r.debit) : ''}</td>
                        <td className="num">{r.credit ? money(r.credit) : ''}</td>
                        <td className={`num cell-strong${r.balance < 0 ? ' neg' : ''}`}>{money(r.balance)}</td>
                      </tr>
                    ))}

                    <tr style={{ borderTop: '2px solid var(--line, currentColor)' }}>
                      <td colSpan={3} className="cell-strong">
                        الإجمالي والرصيد الختامي
                      </td>
                      <td className="num cell-strong">{money(data.totals.debit)}</td>
                      <td className="num cell-strong">{money(data.totals.credit)}</td>
                      <td className={`num cell-strong${data.closing < 0 ? ' neg' : ''}`}>{money(data.closing)}</td>
                    </tr>
                  </tbody>
                )}
              </table>
            </div>

            {data && rows.length === 0 && (
              <p className="tiny mute" style={{ padding: 'var(--sp-3)' }}>
                لا حركة في هذه المدة — الرصيد كما هو من أول المدة.
              </p>
            )}
            {rows.some((r) => r.direct) && (
              <p className="tiny mute" style={{ padding: 'var(--sp-3)' }}>
                الشراء النقدي المباشر — مصروفٌ دُفع للمورد من الخزينة في حينه — يظهر فاتورةً ودفعةً في سطرٍ
                واحد، فلا يغيّر الرصيد.
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/* ==========================================================================
   مُنتقي الطرف — بحثٌ في الخادم بالاسم
   ========================================================================== */

/**
 * قائمةُ العملاء أو الموردين بالبحث.
 *
 * بشكل `Combo` نفسه (الزرّ والقائمة المنسدلة)، غير أنّ الخيارات تأتي من
 * الخادم بالبحث: آلافُ العملاء لا تُحمَّل كلُّها لاختيار واحد.
 */
function PartyPicker({
  side,
  value,
  onPick,
}: {
  side: Side;
  value: string;
  onPick: (id: string) => void;
}) {
  const config = SIDES[side];
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const q = useDebounced(term.trim());
  const boxRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    searchRef.current?.focus();
    const onDown = (e: MouseEvent) => {
      if (!boxRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const results = useQuery({
    queryKey: ['statement-party', side, q],
    queryFn: () =>
      api<{ items: Array<{ id: string; name: string }> }>(`${config.search}${qs({ q: q || undefined, pageSize: 20 })}`),
    enabled: open,
  });
  const items = results.data?.items ?? [];

  const pick = (id: string) => {
    onPick(id);
    setOpen(false);
    setTerm('');
  };

  return (
    <div className={`combo${open ? ' is-open' : ''}`} ref={boxRef} style={{ minWidth: 260 }}>
      <button
        type="button"
        className="combo-btn"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={config.pick}
      >
        <span className={`combo-val${value ? '' : ' is-empty'}`}>{value || config.pick}</span>
        <Icon name="chevronDown" size={14} />
      </button>

      {open && (
        <div className="combo-pop">
          <div className="combo-search">
            <Icon name="search" size={13} />
            <input
              ref={searchRef}
              className="combo-input"
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder={`ابحث باسم ${config.party}`}
              onKeyDown={(e) => {
                if (e.key !== 'Enter') return;
                e.preventDefault();
                if (items[0]) pick(items[0].id);
              }}
            />
          </div>
          <ul className="combo-list" role="listbox">
            {results.isPending && <li className="combo-empty">جارٍ البحث…</li>}
            {results.data && items.length === 0 && <li className="combo-empty">لا نتائج.</li>}
            {items.map((p) => (
              <li key={p.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={p.name === value}
                  className={`combo-opt${p.name === value ? ' is-on' : ''}`}
                  onClick={() => pick(p.id)}
                >
                  <span className="combo-tick">{p.name === value && <Icon name="check" size={13} />}</span>
                  {p.name}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
